import { ApiError, useAuthMe } from '@/api/hooks/auth';
import { signIn } from '@/lib/auth-actions';
import { encodeReturnUrl } from '@/lib/return-url';
import { useEffect } from 'react';

/**
 * Sole observer of the mid-session re-auth trigger. Mounted exactly once
 * inside `App` so the `/api/auth/me` 401 signal fans out to one
 * `signinRedirect` call, not N (one per `usePrincipal` consumer). The
 * codebase has ~20 such consumers — `RootGate`, `LandingRoute`,
 * `RequirePermissions`, `UserMenu`, `nav-config`, plus per-page hooks —
 * and `oidc-client-ts` does NOT coalesce concurrent `signinRedirect` calls.
 * Concentrating the side effect here is the structural fix.
 *
 * Two layers of idempotency protect the single callsite:
 *
 *  1. Single subscriber. Only this component reads `useAuthMe()`'s error
 *     for the purpose of firing `signIn`. The other consumers read the
 *     same React Query cache entry for principal state and never emit a
 *     redirect themselves.
 *  2. The `_redirecting` module flag in `lib/oidc.ts`. The first
 *     `signinRedirect` navigates the page away; any re-run of this effect
 *     (React `StrictMode` dev double-invoke, a dep change in the brief
 *     window before the browser tears down) short-circuits on that flag —
 *     no MSAL `inProgress` state machine involved.
 *
 * Boot-time unauth (no cached OIDC user at all) is handled separately by
 * `AuthedLayout`'s effect — that path is route-scoped (public surfaces
 * like `/` and `/auth/login` deliberately do NOT force sign-in) and
 * doesn't need to read the principal cache. Both effects share the same
 * `_redirecting` guard so they cannot stomp each other.
 *
 * `returnUrl` reads from `window.location` rather than `useLocation()`
 * because this component lives above `RouterProvider` in the tree
 * (mounted in `App`) — there is no router context here, and the values
 * the browser will navigate back to are exactly the window's values.
 */
export function AuthDriver(): null {
  const { error } = useAuthMe();

  const unauthorized = error instanceof ApiError && error.status === 401;

  useEffect(() => {
    if (!unauthorized) return;
    const returnUrl = encodeReturnUrl({
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    });
    void signIn(returnUrl);
  }, [unauthorized]);

  return null;
}
